import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardCheck, UserPlus } from "lucide-react";
import {
  Button,
  EmptyState,
  SkeletonList,
  StatusBadge,
  Tabs,
} from "@/design-system/components/ui";
import { prequalSiteApi, type PrequalCompletions, type PrequalSite } from "../api/prequalSite";
import { errorMessage, type PrequalView } from "./prequalification/panelProps";
import { PrequalScopeControl } from "./prequalification/PrequalScopeControl";
import { CompletionsPanel } from "./prequalification/CompletionsPanel";
import { CompletionDetailDrawer } from "./prequalification/CompletionDetailDrawer";
import { InvitesPanel } from "./prequalification/InvitesPanel";
import { DraftsPanel } from "./prequalification/DraftsPanel";
import { NewPrequalificationWizard } from "./prequalification/NewPrequalificationWizard";

// Partner Brand Ownership — the "Prequalification" tab, shared by the Broker
// and Brokerage portals.
//
// This file is a SHELL. It owns four things and nothing else:
//
//   1. Resolving the caller's site. The completions read doubles as the site
//      resolver — `site === null` is the "nothing linked yet" state, not an
//      error — so it runs on mount before any panel exists, and its result is
//      handed to the completions panel as a seed rather than fetched twice.
//   2. The tab bar, mirrored to `?view=`.
//   3. The ONE Mine/Everyone scope that governs all three lists.
//   4. The `?completion=` drill-in, mounted as a DRAWER alongside the tab. It
//      used to replace the whole tab, tab bar included, which meant reading one
//      row cost the partner the list behind it — their page, their search and
//      their scope — and coming back was a re-navigation rather than a return.
//      The trigger and the deep link are unchanged.
//
// Everything that renders a row — the completions list, the invitations list,
// the in-progress list, and the send wizard — lives in ./prequalification/ and
// is imported here. This file used to hold all of it, at a thousand lines, and
// three lists that co-mounted meant three fetches on every arrival regardless
// of which one the partner had come to look at. Only the active panel mounts
// now; that is the whole point of the tab bar, and un-gating it would put the
// cost straight back.
//
// The server resolves the site from the authenticated session, so nothing here
// ever names a broker, brokerage, or brand id — including on the send path.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query (plain useState loads, mirroring the other
// portal tabs). Writes go through useSubmittingMutation inside the panels.

/** The inner lists, in the order they appear. Tab ids ARE the `?view=` values,
 *  so there is no second vocabulary to keep in step. */
const VIEW_TABS: { id: PrequalView; label: string }[] = [
  { id: "completions", label: "Completions" },
  { id: "invitations", label: "Invitations" },
  { id: "in-progress", label: "In progress" },
];

// Deliberately NO count badges on the tab labels (D9). A count on a tab is a
// promise that the number is live and scoped to what you would see if you
// clicked it — and with only the active panel mounted, no unmounted tab knows
// its own total. A stale or scope-mismatched badge is worse than none.

function isPrequalView(value: string | null): value is PrequalView {
  return VIEW_TABS.some((t) => t.id === value);
}

/** A short line naming the site these completions came from, so a broker on an
 *  inherited firm site is never confused about whose leads these are. */
function SiteBanner({ site }: { site: PrequalSite }) {
  return (
    <p className="m-0 text-small text-gray-mid" data-testid="prequal-site-banner">
      {site.ownership === "Inherited" ? "Your firm's site" : "Your site"}
      {": "}
      <span className="font-semibold text-onyx">{site.displayName}</span>
      <span aria-hidden> · </span>
      <span>/prequalify/{site.slug}</span>
      {!site.isActive && (
        <>
          <span aria-hidden> · </span>
          <StatusBadge tone="muted">Inactive</StatusBadge>
        </>
      )}
    </p>
  );
}

export function PrequalificationTab() {
  const [data, setData] = useState<PrequalCompletions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openId = params.get("completion");

  // The shared scope. Held here rather than in each panel so the three lists
  // can never be showing different populations, and defaulted to the WIDE end
  // — see PrequalScopeControl for why "Mine" would hide organic leads.
  const [mineOnly, setMineOnly] = useState(false);

  const [wizardOpen, setWizardOpen] = useState(false);

  // Bumped after a successful mailed send, and used as the invitations panel's
  // `key`. The panel owns its own load, so the shell cannot reach in and
  // refresh it; remounting is the portal's established way to make a sibling
  // re-read, and it costs nothing here because the panel was almost certainly
  // not mounted a moment ago anyway.
  const [sendNonce, setSendNonce] = useState(0);

  // `?view=` is the single source of truth — no mirrored useState, so a
  // back-button press or a pasted deep link cannot disagree with what is
  // painted. An absent or unrecognised value resolves to Completions rather
  // than erroring: a bad `?view=` is a typo, not a failure.
  const view: PrequalView = isPrequalView(params.get("view")) ? (params.get("view") as PrequalView) : "completions";

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const result = await prequalSiteApi.completions();
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled)
          setError(errorMessage(err, "Couldn't load your prequalification completions."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Both writers copy the CURRENT params and touch one key. That is what keeps
  // `?tab=prequalification` (owned by PortalDashboardShell, which does exactly
  // the same thing in the other direction) and `?view=` alive across a drill-in
  // and back out again — so Back lands on the list the partner came from
  // rather than snapping to the default tab.
  const setOpen = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("completion", id);
    else updated.delete("completion");
    setParams(updated, { replace: true });
  };

  const setView = (next: PrequalView) => {
    const updated = new URLSearchParams(params);
    updated.set("view", next);
    setParams(updated, { replace: true });
  };

  // A mailed invite has just gone out. Switch to Invitations and remount it:
  // the partner's next question is "did that land", and answering it silently
  // on a tab they are not looking at is the same as not answering it. The
  // wizard is not closed from here — it owns its own success state and its own
  // close, and this all happens behind it.
  const handleSent = () => {
    setSendNonce((n) => n + 1);
    setView("invitations");
  };

  // The tab itself, unaware of the drill-in. Extracted so the drawer can be
  // mounted ALONGSIDE whichever of these states is showing instead of being
  // repeated in each of them — including the loading one, so a pasted
  // `?completion=` link opens the drawer immediately rather than waiting on a
  // list read it does not depend on. The drawer loads itself by id.
  const renderTabBody = () => {
    if (error) {
      return (
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      );
    }

    if (!data) return <SkeletonList rows={4} data-testid="prequal-loading" />;

    // No site resolves for this account — not an error, and never somebody
    // else's completions. Nothing below this point mounts, so no panel fires a
    // read the server would answer empty and no send button appears that the
    // server would 403.
    if (!data.site) {
      return (
        <EmptyState
          icon={ClipboardCheck}
          title="No prequalification site linked yet"
          description="When Phoenix links a prequalification site to you — your own, or your firm's shared site — everyone who completes it will show up here. Ask your Phoenix contact to get one set up."
        />
      );
    }

    const site = data.site;

    return (
      <section aria-label="Prequalification" className="flex flex-col gap-space-4">
        <SiteBanner site={site} />

        <div className="flex flex-wrap items-center justify-between gap-space-3">
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={() => setWizardOpen(true)}
            data-testid="prequal-new"
          >
            <UserPlus size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
            New Prequalification
          </Button>
          <PrequalScopeControl mineOnly={mineOnly} onChange={setMineOnly} />
        </div>

        <Tabs
          tabs={VIEW_TABS}
          activeId={view}
          onChange={(id) => setView(id as PrequalView)}
          ariaLabel="Prequalification lists"
        >
          {view === "completions" && (
            <CompletionsPanel mineOnly={mineOnly} initial={data} onOpen={setOpen} />
          )}
          {view === "invitations" && <InvitesPanel key={sendNonce} mineOnly={mineOnly} />}
          {view === "in-progress" && <DraftsPanel mineOnly={mineOnly} />}
        </Tabs>

        <NewPrequalificationWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          siteName={site.displayName}
          onSent={handleSent}
        />
      </section>
    );
  };

  return (
    <>
      {renderTabBody()}
      {/* Mounted conditionally, so mounting IS opening — no `open` prop to
          disagree with `?completion=`, and each open starts a fresh load.
          `setOpen(null)` clears that ONE key, so `?view=` (and the outer
          `?tab=`) survive and closing returns to the list underneath. */}
      {openId && (
        <CompletionDetailDrawer completionId={openId} onClose={() => setOpen(null)} />
      )}
    </>
  );
}
